import React, { useContext, useMemo } from 'react';

import { withStyles } from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Divider from '@material-ui/core/Divider';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Link from '@material-ui/core/Link';

import InputLabel from '../../../inputfields/InputLabel';
import ListDropDown from '../../../inputfields/ListDropDown';
import { DAGSDOS_UNITS_MAP } from '../../../constants-ts';
import { FieldHistoryIcon } from '../FieldHistoryIcon';
import FloatInput from '../FloatInput';
import { FloatInputDebounced } from '../FloatInput';
import ThreeWaySwitch from '../../../inputfields/ThreeWaySwitch';
import IProduct, { NutritionalUnit } from '../../../../models/product';
import { KeycloakContext } from 'components/Secured';
import useViewport from 'hooks/useViewports';
import { isBlank, toSafeFloat } from 'utils/helpers-ts';
import TagLabel from 'components/TagLabel';
import { useExpandableTreeView } from '../validation-context/useExpandableTreeView';
import RequiredAsterisk from '../validation-context/RequiredAsterisk';
import MissingFieldHint from '../validation-context/MissingFieldHint';
import AiFieldVerdict from '../AiFieldVerdict';
import { shouldShowDailyDose } from '../utils/aiReviewFields';

interface INutritionProps {
  readOnly?: boolean;
  state: IProduct;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  approve?: boolean;
  isSupplement?: boolean;
  classes?: any;
}

const styles = (theme: any) => ({
  asterisk: {
    color: theme.palette.error.main,
  },
});

// Parses a nonnegative decimal ("3", "1.40", "2,1") into its digits and the number of
// significant decimals (trailing zeros ignored). Returns undefined for anything else.
const parseEnergyDecimal = (value: string | number) => {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(String(value).trim().replace(',', '.'));
  if (!match || !(match[1] || match[2])) return undefined;
  const fraction = (match[2] || '').replace(/0+$/, '');
  return { digits: Number(`${match[1]}${fraction}`), decimals: fraction.length };
};

export const isEnergyError = (state?: IProduct) => {
  if (isBlank(state?.energi_kj) || isBlank(state?.energi_kcal)) return false;

  const kJ = parseEnergyDecimal(state!.energi_kj!);
  const kcal = parseEnergyDecimal(state!.energi_kcal!);
  if (!kJ || !kcal) return true;

  // Compare in integer units of 10^-scale so boundaries are exact (no float rounding).
  // Each value may be off by half of its last decimal unit: 3 → ±0.5, 1.4 → ±0.05.
  const scale = Math.max(kJ.decimals, kcal.decimals) + 1;
  const toUnits = ({ digits, decimals }: { digits: number; decimals: number }) => digits * 10 ** (scale - decimals);
  const toAllowance = ({ decimals }: { decimals: number }) => 5 * 10 ** (scale - decimals - 1);
  const K = toUnits(kJ), dK = toAllowance(kJ);
  const C = toUnits(kcal), dC = toAllowance(kcal);

  // Pass when 4 × kcal <= kJ <= 4.5 × kcal holds for some values within the allowances.
  return !(K + dK >= 4 * Math.max(0, C - dC) && 2 * (K - dK) <= 9 * (C + dC));
}

export const CheckEnergyError = ({ readOnly, state }: { readOnly?: boolean, state?: IProduct }) => {
  if (!isEnergyError(state)) return null;

  return (
    <TagLabel space type='error' readOnly={readOnly}>
      <span>Det verkar som om att du angivit värden för kJ och kcal som inte stämmer överens. Stäm gärna av så att värdena stämmer (1 kcal = 4,184 kJ) eller hör av dig till{` `}</span>
      <Link underline="always" target="_blank" href="mailto:support@sygrid.com">
        support@sygrid.com
      </Link>
      <span>{` `}så hjälper vi till!</span>
    </TagLabel>
  );
}

const NUTRITION_WEIGHT_CAP_ERROR = 'Mängden av enskilda näringsvärden (g) kan inte överstiga 100 g.';
export const NUTRITION_FAT_SUB_COMPONENTS_ERROR = 'Totala värdet för fett kan inte vara mindre än summan av dess underkomponenter (varav).';
export const NUTRITION_CARB_SUB_COMPONENTS_ERROR = 'Totala värdet för kolhydrater kan inte vara mindre än summan av dess underkomponenter (varav).';
const NUTRITION_TOTAL_SUM_ERROR = 'Den totala summan av fett, kolhydrater, fiber, protein och salt kan inte överstiga 100 g.';

type NutritionErrors = Partial<Record<keyof IProduct, string[]>>;

export const getNutritionErrors = (state: IProduct | undefined, selectedNutritionalUnit: string) => {
  const errors: NutritionErrors = {};
  const isGram = selectedNutritionalUnit === NutritionalUnit.GRAM;

  const getValue = (field: keyof IProduct): number => {
    return toSafeFloat(state?.[field] as string | number, 0) ?? 0;
  }

  const addError = (field: keyof IProduct, error: string) => {
    errors[field] = errors[field] || [];
    errors[field]?.push(error);
  }

  const weightFields: (keyof IProduct)[] = [
    'fett',
    'mattatFett',
    'enkelomattatFett',
    'flerromattatFett',
    'kolhydrat',
    'sockerarter',
    'polyoler',
    'starkelse',
    'fiber',
    'protein',
    'salt',
  ];
  const topLevelWeightFields: (keyof IProduct)[] = ['fett', 'kolhydrat', 'fiber', 'protein', 'salt'];
  if (isGram) {
    let hasCapError = false;
    // Check: any weight field > 100
    weightFields.forEach((field) => {
      if (getValue(field) > 100) {
        addError(field, NUTRITION_WEIGHT_CAP_ERROR);
        hasCapError = true;
      }
    });

    // Check: Fett + Kolhydrat + Fiber + Protein + Salt > 100 (top-level only)
    if (!hasCapError) {
      const topLevelTotal = topLevelWeightFields.reduce((sum, field) => sum + getValue(field), 0);
      if (topLevelTotal > 100) {
        topLevelWeightFields.forEach((field) => addError(field, NUTRITION_TOTAL_SUM_ERROR));
      }
    }
  }

  const fettSubFields: (keyof IProduct)[] = ['mattatFett', 'enkelomattatFett', 'flerromattatFett'];
  const fettFields: (keyof IProduct)[] = ['fett', ...fettSubFields];
  const hasFettCapError = isGram && fettFields.some((field) => getValue(field) > 100);

  if (!isBlank(state?.fett) && !hasFettCapError) {
    // Check: Mättat fett + Enkelomättat fett + Fleromättat fett > Fett
    const fettSubFieldsTotal = fettSubFields.reduce((sum, field) => sum + getValue(field), 0);

    if (fettSubFieldsTotal > getValue('fett')) {
      addError('fett', NUTRITION_FAT_SUB_COMPONENTS_ERROR);

      fettSubFields.forEach((field) => {
        if (!isBlank(state?.[field])) addError(field, NUTRITION_FAT_SUB_COMPONENTS_ERROR)
      });
    }
  }

  const kolhydratSubFields: (keyof IProduct)[] = ['sockerarter', 'polyoler', 'starkelse'];
  const kolhydratFields: (keyof IProduct)[] = ['kolhydrat', ...kolhydratSubFields];
  const hasKolhydratCapError = isGram && kolhydratFields.some((field) => getValue(field) > 100);

  if (!isBlank(state?.kolhydrat) && !hasKolhydratCapError) {
    // Check: Sockerarter + Polyoler + Stärkelse > Kolhydrat
    const kolhydratSubFieldsTotal = kolhydratSubFields.reduce((sum, field) => sum + getValue(field), 0);

    if (kolhydratSubFieldsTotal > getValue('kolhydrat')) {
      addError('kolhydrat', NUTRITION_CARB_SUB_COMPONENTS_ERROR);

      kolhydratSubFields.forEach((field) => {
        if (!isBlank(state?.[field])) addError(field, NUTRITION_CARB_SUB_COMPONENTS_ERROR)
      });
    }
  }

  return errors;
}

export const getNutritionErrorMessages = (errors: NutritionErrors) => {
  const allErrors = Object.values(errors).flat();

  return [
    NUTRITION_WEIGHT_CAP_ERROR,
    NUTRITION_FAT_SUB_COMPONENTS_ERROR,
    NUTRITION_CARB_SUB_COMPONENTS_ERROR,
    NUTRITION_TOTAL_SUM_ERROR,
  ].filter((error) => allErrors.includes(error));
}

export const isNutritionError = (state: IProduct | undefined, selectedNutritionalUnit: string) => {
  return !!getNutritionErrorMessages(getNutritionErrors(state, selectedNutritionalUnit)).length;
}

const FieldWeightCapError = ({ readOnly, errors, field }: { readOnly?: boolean, errors: NutritionErrors; field: keyof IProduct }) => {
  if (errors[field]?.includes(NUTRITION_WEIGHT_CAP_ERROR)) {
    return (
      <TagLabel space type='error' readOnly={readOnly}>
        <span>{NUTRITION_WEIGHT_CAP_ERROR}</span>
      </TagLabel>
    );
  }
  return null;
}

// The field's own error first, then the failed AI review verdict, below the row of the field.
const FieldMessages = ({ readOnly, errors, field }: { readOnly?: boolean, errors: NutritionErrors; field: keyof IProduct }) => (
  <>
    <FieldWeightCapError readOnly={readOnly} errors={errors} field={field} />
    <AiFieldVerdict field={field} />
  </>
);

export const CheckNutritionError = ({ readOnly, state, selectedNutritionalUnit }: { readOnly?: boolean, state?: IProduct, selectedNutritionalUnit: string }) => {
  const errorMessages = getNutritionErrorMessages(getNutritionErrors(state, selectedNutritionalUnit))
    .filter((error) => error !== NUTRITION_FAT_SUB_COMPONENTS_ERROR && error !== NUTRITION_CARB_SUB_COMPONENTS_ERROR && error !== NUTRITION_WEIGHT_CAP_ERROR);

  if (!errorMessages.length) return null;

  return (
    <>
      {errorMessages.map((error) => (
        <TagLabel key={error} readOnly={readOnly} space type='error'>
          <span>{error}</span>
        </TagLabel>
      ))}
    </>
  );
}

export const Nutrition = ({ readOnly, state, setState, classes, approve, isSupplement }: INutritionProps) => {
  const { smUp, mdUp } = useViewport();
  const keycloakCtx = useContext(KeycloakContext);
  const isSuperuser = keycloakCtx?.keycloak?.hasResourceRole('superuser') as boolean;

  const selectedNutritionalUnit =
    !isSupplement && state.nutritional_unit === 'DAGSDOS' ? 'GRAM' : state.nutritional_unit || 'GRAM';
  const nutritionErrors = useMemo(() => {
    return getNutritionErrors(state, selectedNutritionalUnit);
  }, [selectedNutritionalUnit, state]);

  const hasFatSubComponentError = useMemo(() => {
    return Object.values(nutritionErrors).some((fieldErrors) =>
      fieldErrors?.includes(NUTRITION_FAT_SUB_COMPONENTS_ERROR)
    );
  }, [nutritionErrors]);

  const hasCarbSubComponentError = useMemo(() => {
    return Object.values(nutritionErrors).some((fieldErrors) =>
      fieldErrors?.includes(NUTRITION_CARB_SUB_COMPONENTS_ERROR)
    );
  }, [nutritionErrors]);

  // TODO any
  const handleChange = (event: any) => {
    const stateName = event.target.name;
    let stateValue = event.target.value;

    // Switching the basis away from DAGSDOS clears the dose basis so it isn't left dangling on a
    // per-100 product (backend save coercion mirrors this).
    const nextState = { ...state, [stateName]: stateValue };
    if (stateName === 'nutritional_unit' && stateValue !== 'DAGSDOS') {
      nextState.dagsdosAmount = undefined;
      nextState.dagsdosUnit = '';
    }
    setState(nextState);
  };

  const labelStyle: React.CSSProperties = { flex: mdUp ? '0 0 200px' : '0 0 175px' };
  const inputStyle: React.CSSProperties = { flex: mdUp ? '0 0 150px' : 1 };

  const renderDRI = (value: number, dri: number, grow?: boolean) => {
    if (!value) return null;

    return (
      <div
        style={{
          fontSize: 14,
          fontWeight: 400,
          alignSelf: 'center',
          width: grow && smUp ? 'calc(100% - 420px)' : '',
          marginLeft: 'auto',
          marginBottom: grow ? 0 : 8,
        }}
      >
        &nbsp;{value ? `(${Math.round((value / dri) * 100)}% av DRI)` : ''}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: mdUp ? 'row' : 'column', flex: 1 }}>
        <h3 style={{ ...(mdUp ? labelStyle : {}), marginTop: 9, marginBottom: mdUp ? 9 : 0 }}>
          Näringsvärde per
          <RequiredAsterisk field="nutritional_unit" />
        </h3>

        <FormControl disabled={readOnly} component="fieldset">
          <RadioGroup
            id="nutritional_unit"
            row={smUp}
            aria-label="nutritional_unit"
            value={selectedNutritionalUnit}
            name="nutritional_unit"
            onChange={handleChange}
          >
            <FormControlLabel
              value="GRAM"
              control={<Radio color="primary" />}
              label="100 gram"
              style={{ marginLeft: 0 }}
            />
            <FormControlLabel value="MILLILITER" control={<Radio color="primary" />} label="100 milliliter" />
            {isSupplement && (
              <FormControlLabel value="DAGSDOS" control={<Radio color="primary" />} label="Dagsdos" />
            )}
          </RadioGroup>
          <MissingFieldHint field="nutritional_unit" hideAiVerdict />
        </FormControl>
      </div>
      {/* Under the whole row, from its start, not in the column of the radios. */}
      <AiFieldVerdict field="nutritional_unit" />

      {shouldShowDailyDose(state, isSupplement) && (
        <div
          style={{
            display: 'flex',
            flexDirection: mdUp ? 'row' : 'column',
            alignItems: mdUp ? 'flex-start' : 'stretch',
            flex: 1,
            marginBottom: 8,
            gap: 8,
          }}
        >
          <h3 style={mdUp ? labelStyle : { flex: 0, marginBottom: 0 }}>
            Dagsdos:
            <RequiredAsterisk field="dagsdosAmount" />
          </h3>
          {/* Mängd + enhet (t.ex. 2 kapslar) — basen näringsvärdena anges per. Mirrors the
              nettokvantitet amount+unit pattern; unit options from DAGSDOS_UNITS_MAP. */}
          {/* dense={false} → margin="normal", so the field matches the outlined dropdown's
              height; paddingTop on the dropdown aligns its top with the field's marginTop —
              same pairing as nettokvantitet (descriptive.js). */}
          <FloatInput
            readOnly={readOnly}
            name="dagsdosAmount"
            aiVerdict="none"
            required
            dense={false}
            state={state}
            setState={setState}
            forceRed={!!state?.showRed && isBlank(state.dagsdosAmount)}
            style={{ flex: mdUp ? '0 0 120px' : 1 }}
          />
          <ListDropDown
            readOnly={readOnly}
            label="Enhet"
            listData={DAGSDOS_UNITS_MAP}
            value={state.dagsdosUnit || ''}
            handleChange={e => setState({ ...state, dagsdosUnit: String(e.target.value || '') })}
            targetName="dagsdosUnit"
            hideAiVerdict
            forceRed={!!state?.showRed && isBlank(state.dagsdosUnit)}
            style={{ flex: mdUp ? '0 0 180px' : 1, paddingTop: 5 }}
          />
        </div>
      )}
      {shouldShowDailyDose(state, isSupplement) && (
        <>
          <AiFieldVerdict field="dagsdosAmount" />
          <AiFieldVerdict field="dagsdosUnit" />
        </>
      )}
      <Divider />

      <div style={{ display: 'flex', flexDirection: mdUp ? 'row' : 'column', flex: 1 }}>
        <h3 style={mdUp ? labelStyle : { flex: 0, marginBottom: 0 }}>
          Energi:
        </h3>
        <div style={{ flex: 1, display: 'flex', gap: 4 }}>
          <div>
            <FloatInputDebounced
              readOnly={readOnly}
              name="energi_kj"
              aiVerdict="none"
              required={true}
              state={state}
              setState={setState}
              style={inputStyle}
              forceRed={isEnergyError(state)}
            />
            {renderDRI(state.energi_kj || 0, 8400)}
          </div>
          <h3 style={{ whiteSpace: 'nowrap' }}>&nbsp;kJ /&nbsp;</h3>

          <div>
            <FloatInputDebounced
              readOnly={readOnly}
              name="energi_kcal"
              aiVerdict="none"
              required={true}
              state={state}
              setState={setState}
              style={inputStyle}
              forceRed={isEnergyError(state)}
            />
            {renderDRI(state.energi_kcal || 0, 2000)}
          </div>
          <h3>&nbsp;kcal</h3>
        </div>
      </div>
      <CheckEnergyError readOnly={readOnly} state={state} />
      <AiFieldVerdict field="energi_kj" />
      <AiFieldVerdict field="energi_kcal" />
      <Divider />

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          Fett:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="fett"
          aiVerdict="none"
          required={true}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.fett}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="fett" />
          </span>
        )}
        {renderDRI(state.fett || 0, 70, true)}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="fett" />
      <h3 style={{marginBottom: 2, marginTop: 2}}>
        &nbsp;&nbsp;&nbsp;&nbsp;varav:
      </h3>

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          &nbsp;&nbsp;&nbsp;&nbsp;- mättat fett:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="mattatFett"
          aiVerdict="none"
          required={true}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.mattatFett}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="mattatFett" />
          </span>
        )}
        {renderDRI(state.mattatFett || 0, 20, true)}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="mattatFett" />
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          &nbsp;&nbsp;&nbsp;&nbsp;- enkelomättat fett:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="enkelomattatFett"
          aiVerdict="none"
          required={false}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.enkelomattatFett}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="enkelomattatFett" />
          </span>
        )}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="enkelomattatFett" />
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          &nbsp;&nbsp;&nbsp;&nbsp;- fleromättat fett:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="flerromattatFett"
          aiVerdict="none"
          required={false}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.flerromattatFett}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="flerromattatFett" />
          </span>
        )}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="flerromattatFett" />
      {hasFatSubComponentError && (
        <TagLabel space type='error' readOnly={readOnly}>
          <span>{NUTRITION_FAT_SUB_COMPONENTS_ERROR}</span>
        </TagLabel>
      )}

      <div style={{display: 'flex', flexDirection: 'row', flex: 1}}>
        <Divider style={{flex: 0.9}} />
        {approve && state?.approve?.find((x) => x.field === 'nutrition') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            style={{ flex: 0.1, marginBottom: 8 }}
            name="nutrition"
            state={state}
            setState={setState}
          />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
        Kolhydrat:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="kolhydrat"
          aiVerdict="none"
          required={true}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.kolhydrat}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="kolhydrat" />
          </span>
        )}
        {renderDRI(state.kolhydrat || 0, 260, true)}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="kolhydrat" />
      <h3 style={{marginBottom: 2, marginTop: 2}}>
        &nbsp;&nbsp;&nbsp;&nbsp;varav:
      </h3>
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          &nbsp;&nbsp;&nbsp;&nbsp;- sockerarter:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="sockerarter"
          aiVerdict="none"
          required={true}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.sockerarter}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="sockerarter" />
          </span>
        )}
        {renderDRI(state.sockerarter || 0, 90, true)}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="sockerarter" />
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          &nbsp;&nbsp;&nbsp;&nbsp;- polyoler:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="polyoler"
          aiVerdict="none"
          required={false}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.polyoler}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="polyoler" />
          </span>
        )}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="polyoler" />
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          &nbsp;&nbsp;&nbsp;&nbsp;- stärkelse:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="starkelse"
          aiVerdict="none"
          required={false}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.starkelse}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="starkelse" />
          </span>
        )}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="starkelse" />
      {hasCarbSubComponentError && (
        <TagLabel readOnly={readOnly} space type='error'>
          <span>{NUTRITION_CARB_SUB_COMPONENTS_ERROR}</span>
        </TagLabel>
      )}
      <Divider />

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          Fiber:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="fiber"
          aiVerdict="none"
          required={false}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.fiber}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="fiber" />
          </span>
        )}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="fiber" />
      <Divider />

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          Protein:
        </h3>

        <FloatInputDebounced
          readOnly={readOnly}
          name="protein"
          aiVerdict="none"
          required={true}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.protein}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="protein" />
          </span>
        )}
        {renderDRI(state.protein || 0, 50, true)}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="protein" />
      <Divider />

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center' }}>
        <h3 style={labelStyle}>
          Salt:
        </h3>
        <FloatInputDebounced
          readOnly={readOnly}
          name="salt"
          aiVerdict="none"
          required={true}
          state={state}
          setState={setState}
          style={inputStyle}
          forceRed={!!nutritionErrors.salt}
        />
        <h3>&nbsp;g</h3>
        {isSuperuser && (
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center' }}>
            <FieldHistoryIcon name="salt" />
          </span>
        )}
        {renderDRI(state.salt || 0, 6, true)}
      </div>
      <FieldMessages readOnly={readOnly} errors={nutritionErrors} field="salt" />

      <CheckNutritionError readOnly={readOnly} state={state} selectedNutritionalUnit={selectedNutritionalUnit} />
      <Divider />
    </>
  );
};

const NutritionBase = withStyles(styles)(Nutrition);

const validValue = (value?: number) => {
  return !!value || value === 0;
}

const NutritionWrapper = ({ state, ...props }: INutritionProps) => {
  const keycloakCtx = useContext(KeycloakContext);
  const isStore = keycloakCtx.keycloak.hasResourceRole('store');

  const expand = isStore && (
    validValue(state.energi_kj) ||
    validValue(state.energi_kcal) ||
    validValue(state.fett) ||
    validValue(state.mattatFett) ||
    validValue(state.enkelomattatFett) ||
    validValue(state.flerromattatFett) ||
    validValue(state.kolhydrat) ||
    validValue(state.sockerarter) ||
    validValue(state.polyoler) ||
    validValue(state.starkelse) ||
    validValue(state.fiber) ||
    validValue(state.protein) ||
    validValue(state.salt)
  );

  const { expanded, onNodeToggle } = useExpandableTreeView('nutrition', expand ? ['root'] : []);

  const labelElement = (
    <InputLabel
      heading="Näringsdeklaration"
      text="Obligatorisk, i de flesta fall, men det finns några undantag. Den obligatoriska näringsdeklarationen ska innehålla uppgifter om energivärde, och mängden av fett, mättat fett, kolhydrat, sockerarter, protein och salt."
      link="http://kontrollwiki.livsmedelsverket.se/artikel/46/naringsdeklaration"
    />
  );

  if (!isStore) {
    return (
      <>
        {labelElement}
        <NutritionBase {...props} state={state} />
      </>
    );
  }

  return (
    <Box my={1}>
      <TreeView
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        onNodeToggle={onNodeToggle}
      >
        <TreeItem nodeId="root" label={labelElement}>
          <NutritionBase {...props} state={state} />
        </TreeItem>
      </TreeView>
    </Box>
  );
}

export default NutritionWrapper;
